import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../../lib/supabaseClient";
import { useUserProfile } from "../../contexts/UserProfileContext";

interface Item {
  id: string;
  name: string;
  category_id: string | null;
  created_at: string | null;
}

interface Category {
  id: string;
  name: string;
}

export default function PacklistTemplateItemsAdmin() {
  const { profile } = useUserProfile();
  const { id: templateId } = useParams();
  const [templateName, setTemplateName] = useState("");
  const [items, setItems] = useState<Item[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [globalItems, setGlobalItems] = useState<Item[]>([]);
  const [addingItemId, setAddingItemId] = useState<string | null>(null);
  const [nameFilter, setNameFilter] = useState("");
  const [collapsedCategories, setCollapsedCategories] = useState<
    Record<string, boolean>
  >({});

  const getCategoryName = (id: string | null) =>
    (id ? categories.find((c) => c.id === id)?.name : undefined) || "Unbekannt";

  useEffect(() => {
    if (!templateId) return;
    supabase
      .from("packlist_templates")
      .select("name")
      .eq("id", templateId)
      .single()
      .then(({ data }) => setTemplateName(data?.name ?? ""));
  }, [templateId]);

  useEffect(() => {
    if (!profile || profile.role !== "admin" || !templateId) return;

    const fetchData = async () => {
      setLoading(true);

      const [relRes, catRes, allItemsRes] = await Promise.all([
        supabase
          .from("packlist_template_items")
          .select("item:packlist_items(id, name, category_id, created_at)")
          .eq("template_id", templateId),
        supabase.from("packlist_categories").select("id, name"),
        supabase
          .from("packlist_items")
          .select("id, name, category_id, created_at"),
      ]);

      const relItems = relRes.data?.map((rel: any) => rel.item) ?? [];
      const allItems = allItemsRes.data ?? [];

      const assignedIds = new Set(relItems.map((i: Item) => i.id));
      const unassignedItems = allItems.filter(
        (i: Item) => !assignedIds.has(i.id)
      );

      setItems(relItems);
      setGlobalItems(unassignedItems);
      setCategories(catRes.data ?? []);

      const collapsed = Object.fromEntries(
        (catRes.data ?? []).map((c) => [c.name, true])
      );
      setCollapsedCategories(collapsed);

      setLoading(false);
    };

    fetchData();
  }, [profile, templateId]);

  const removeFromTemplate = async (itemId: string) => {
    if (!templateId) return;
    const { error } = await supabase
      .from("packlist_template_items")
      .delete()
      .eq("template_id", templateId)
      .eq("item_id", itemId);

    if (!error) {
      const removed = items.find((i) => i.id === itemId);
      setItems((prev) => prev.filter((i) => i.id !== itemId));
      if (removed) setGlobalItems((prev) => [...prev, removed]);
    }
  };

  const addToTemplate = async (itemId: string) => {
    if (!templateId) return;
    setAddingItemId(itemId);
    const { error } = await supabase
      .from("packlist_template_items")
      .insert([{ template_id: templateId, item_id: itemId }]);

    if (!error) {
      const added = globalItems.find((i) => i.id === itemId);
      if (added) {
        setItems((prev) => [...prev, added]);
        setGlobalItems((prev) => prev.filter((i) => i.id !== itemId));
      }
    }

    setAddingItemId(null);
  };

  const groupedSorted = (list: Item[]) =>
    Object.entries(
      list
        .filter((item) =>
          item.name.toLowerCase().includes(nameFilter.trim().toLowerCase())
        )
        .sort((a, b) =>
          a.name.localeCompare(b.name, "de", { sensitivity: "base" })
        )
        .reduce((acc, item) => {
          const cat = getCategoryName(item.category_id);
          if (!acc[cat]) acc[cat] = [];
          acc[cat].push(item);
          return acc;
        }, {} as Record<string, Item[]>)
    );

  return (
    <div className="max-w-4xl mx-auto mt-10 p-6">
      <h1 className="text-2xl font-bold text-sky-400 mb-6">
        📦 Zugeordnete Items zur Vorlage:{" "}
        <span className="text-slate-100">{templateName}</span>
      </h1>

      <div className="mb-4">
        <label className="block text-sm font-medium text-slate-400 mb-1">
          🔍 Nach Name suchen...
        </label>
        <input
          type="text"
          value={nameFilter}
          onChange={(e) => setNameFilter(e.target.value)}
          className="border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded px-3 py-2 w-full outline-none focus:border-sky-400 placeholder:text-slate-600 text-sm"
        />
      </div>

      <div className="flex gap-4 mb-4">
        <button
          onClick={() =>
            setCollapsedCategories(
              Object.fromEntries(categories.map((cat) => [cat.name, false]))
            )
          }
          className="text-sm text-sky-400 hover:text-sky-300 transition-colors"
        >
          🔽 Alle aufklappen
        </button>
        <button
          onClick={() =>
            setCollapsedCategories(
              Object.fromEntries(categories.map((cat) => [cat.name, true]))
            )
          }
          className="text-sm text-sky-400 hover:text-sky-300 transition-colors"
        >
          🔼 Alle zuklappen
        </button>
      </div>

      {/* Zugeordnete Items */}
      <h2 className="font-semibold mb-2 text-slate-100">📋 Zugeordnete Items</h2>
      {loading ? (
        <p className="text-slate-400">Lade Daten...</p>
      ) : (
        groupedSorted(items).map(([cat, items]) => (
          <div key={cat} className="mb-6">
            <div
              className="flex items-center justify-between cursor-pointer group"
              onClick={() =>
                setCollapsedCategories((prev) => ({
                  ...prev,
                  [cat]: !prev[cat],
                }))
              }
            >
              <h3 className="text-lg font-semibold text-slate-300 mb-2 group-hover:text-sky-400 transition-colors">
                {collapsedCategories[cat] ? "▲" : "▼"} {cat}
              </h3>
            </div>
            {!collapsedCategories[cat] && (
              <ul className="space-y-3">
                {items.map((item) => (
                  <li
                    key={item.id}
                    className="bg-[#1e2639] border border-[#2a3347] p-3 rounded flex justify-between items-center"
                  >
                    <div className="font-semibold text-slate-100">{item.name}</div>
                    <button
                      onClick={() => removeFromTemplate(item.id)}
                      className="text-sm bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 transition-colors"
                    >
                      Entfernen
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))
      )}

      {/* Globale Items */}
      <h2 className="font-semibold mt-10 mb-2 text-slate-100">➕ Verfügbare globale Items</h2>
      {groupedSorted(globalItems).length === 0 ? (
        <p className="text-slate-500">Keine weiteren globalen Items verfügbar.</p>
      ) : (
        groupedSorted(globalItems).map(([cat, items]) => (
          <div key={cat} className="mb-6">
            <div
              className="flex items-center justify-between cursor-pointer group"
              onClick={() =>
                setCollapsedCategories((prev) => ({
                  ...prev,
                  [cat]: !prev[cat],
                }))
              }
            >
              <h3 className="text-lg font-semibold text-slate-300 mb-2 group-hover:text-sky-400 transition-colors">
                {collapsedCategories[cat] ? "▲" : "▼"} {cat}
              </h3>
            </div>
            {!collapsedCategories[cat] && (
              <ul className="space-y-3">
                {items.map((item) => (
                  <li
                    key={item.id}
                    className="bg-[#1e2639] border border-[#2a3347] p-3 rounded flex justify-between items-center"
                  >
                    <div className="font-semibold text-slate-100">{item.name}</div>
                    <button
                      onClick={() => addToTemplate(item.id)}
                      disabled={addingItemId === item.id}
                      className="bg-sky-500 text-white px-3 py-1 rounded hover:bg-sky-600 disabled:opacity-50 transition-colors"
                    >
                      {addingItemId === item.id
                        ? "Wird hinzugefügt..."
                        : "Hinzufügen"}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))
      )}
    </div>
  );
}
